import {type OperationVariables, type QueryOptions} from '@apollo/client';

import {apolloClientNoCache} from '../../../../apolloClient';
import {
  BackendLinesDeltaQuery_Dd_Dq_U_BldqDocument,
  type BackendLinesDeltaQuery_Dd_Dq_U_BldqQuery,
  type BackendLinesDeltaQuery_Dd_Dq_U_BldqQueryVariables,
  type BackendLinesInternalIdDeltaQuery_Dd_Dq_U_BldqQuery,
  type BackendLinesInternalIdDeltaQuery_Dd_Dq_U_BldqQueryVariables,
  BucketedRunsDeltaQuery_Dd_Dq_U_BrdqDocument,
  type BucketedRunsDeltaQuery_Dd_Dq_U_BrdqQuery,
  type BucketedRunsDeltaQuery_Dd_Dq_U_BrdqQueryVariables,
  type BucketedRunsInternalIdDeltaQuery_Dd_Dq_U_BrdqQuery,
  type BucketedRunsInternalIdDeltaQuery_Dd_Dq_U_BrdqQueryVariables,
  GroupedBackendLinesDeltaQuery_Dd_Dq_G_BldqDocument,
  type GroupedBackendLinesDeltaQuery_Dd_Dq_G_BldqQuery,
  type GroupedBackendLinesDeltaQuery_Dd_Dq_G_BldqQueryVariables,
  GroupedBucketedRunsDeltaQuery_Dd_Dq_G_BrdqDocument,
  type GroupedBucketedRunsDeltaQuery_Dd_Dq_G_BrdqQuery,
  type GroupedBucketedRunsDeltaQuery_Dd_Dq_G_BrdqQueryVariables,
  GroupedBucketedRunsInternalIdDeltaQuery_Dd_Dq_G_BrdqDocument,
  type GroupedBucketedRunsInternalIdDeltaQuery_Dd_Dq_G_BrdqQuery,
  type GroupedBucketedRunsInternalIdDeltaQuery_Dd_Dq_G_BrdqQueryVariables,
} from '../../../../generated/graphql';
import {type ApolloQueryResult} from '../../../../state/graphql/apolloWithAbort';
import {
  RETRY_AFTER_HOLDER_CONTEXT_KEY,
  RETRY_AFTER_SECONDS_EXTENSION,
  type RetryAfterSecondsHolder,
} from '../../../../util/apolloRetryAfter';
import {
  type RequestCause,
  requestCauseContext,
} from '../../../../util/apolloRetryHeader';
import {propagateErrorsContext} from '../../../../util/errors';
import {BACKEND_LINES_DELTA_QUERY_INTERNAL_ID} from '../queryDocuments/backendLines.query';
import {BUCKETED_RUNS_DELTA_QUERY_INTERNAL_ID} from '../queryDocuments/bucketedRuns.query';
import {type QueryVars, type queryVars} from './queryVars';
import {
  type BackendLinesQueryPromise,
  type BucketedQueryPromise,
  type GroupedBackendLinesQueryPromise,
} from './types';

type DeltaQueryResult<TData> = ApolloQueryResult<TData> & {
  extensions?: Record<string, unknown>;
};

/**
 * client.query() drops FetchResult.extensions. Pass a mutable holder through
 * context so retryAfterExtensionsLink can record Retry-After, then reattach it
 * onto the returned result for extractRetryAfterSeconds.
 */
async function queryDeltaWithRetryAfter<
  TData,
  TVariables extends OperationVariables,
>(options: QueryOptions<TVariables, TData>): Promise<DeltaQueryResult<TData>> {
  const holder: RetryAfterSecondsHolder = {};
  const result = await apolloClientNoCache.query<TData, TVariables>({
    ...options,
    context: {
      ...options.context,
      [RETRY_AFTER_HOLDER_CONTEXT_KEY]: holder,
    },
  });
  if (holder.seconds == null) {
    return result;
  }
  return {
    ...result,
    extensions: {
      [RETRY_AFTER_SECONDS_EXTENSION]: holder.seconds,
    },
  };
}

function deltaQueryContext(cause?: RequestCause) {
  return {...propagateErrorsContext(), ...requestCauseContext(cause)};
}

export function doBackendLinesApiQuery(
  vars: ReturnType<typeof queryVars>,
  currentRuns: string[],
  lastUpdated: string,
  cause?: RequestCause
): BackendLinesQueryPromise {
  return queryDeltaWithRetryAfter<
    BackendLinesDeltaQuery_Dd_Dq_U_BldqQuery,
    BackendLinesDeltaQuery_Dd_Dq_U_BldqQueryVariables
  >({
    context: deltaQueryContext(cause),
    query: BackendLinesDeltaQuery_Dd_Dq_U_BldqDocument,
    fetchPolicy: 'no-cache',
    variables: {
      ...vars,
      currentRuns,
      lastUpdated,
    },
  });
}

export function doGroupedBackendLinesApiQuery(
  vars: ReturnType<typeof queryVars>,
  currentRuns: string[],
  lastUpdated: string,
  cause?: RequestCause
): GroupedBackendLinesQueryPromise {
  return queryDeltaWithRetryAfter<
    GroupedBackendLinesDeltaQuery_Dd_Dq_G_BldqQuery,
    GroupedBackendLinesDeltaQuery_Dd_Dq_G_BldqQueryVariables
  >({
    context: deltaQueryContext(cause),
    query: GroupedBackendLinesDeltaQuery_Dd_Dq_G_BldqDocument,
    fetchPolicy: 'no-cache',
    variables: {
      ...vars,
      currentRuns,
      lastUpdated,
    },
  });
}

export function doBackendLinesApiQueryByInternalId(
  vars: ReturnType<typeof queryVars>,
  currentRuns: string[],
  lastUpdated: string,
  cause?: RequestCause
): BackendLinesQueryPromise {
  return queryDeltaWithRetryAfter<
    BackendLinesInternalIdDeltaQuery_Dd_Dq_U_BldqQuery,
    BackendLinesInternalIdDeltaQuery_Dd_Dq_U_BldqQueryVariables
  >({
    context: deltaQueryContext(cause),
    query: BACKEND_LINES_DELTA_QUERY_INTERNAL_ID,
    fetchPolicy: 'no-cache',
    variables: {
      ...vars,
      currentRuns,
      lastUpdated,
    },
  });
}

export function doBucketedApiQuery(
  vars: ReturnType<typeof queryVars>,
  currentRuns: string[],
  lastUpdated: string,
  cause?: RequestCause
): BucketedQueryPromise {
  return queryDeltaWithRetryAfter<
    BucketedRunsDeltaQuery_Dd_Dq_U_BrdqQuery,
    BucketedRunsDeltaQuery_Dd_Dq_U_BrdqQueryVariables
  >({
    context: deltaQueryContext(cause),
    query: BucketedRunsDeltaQuery_Dd_Dq_U_BrdqDocument,
    fetchPolicy: 'no-cache',
    variables: {
      ...vars,
      currentRuns,
      lastUpdated,
    },
  });
}

export function doBucketedApiQueryByInternalId(
  vars: QueryVars,
  currentRuns: string[],
  lastUpdated: string,
  cause?: RequestCause
): BucketedQueryPromise {
  return queryDeltaWithRetryAfter<
    BucketedRunsInternalIdDeltaQuery_Dd_Dq_U_BrdqQuery,
    BucketedRunsInternalIdDeltaQuery_Dd_Dq_U_BrdqQueryVariables
  >({
    context: deltaQueryContext(cause),
    query: BUCKETED_RUNS_DELTA_QUERY_INTERNAL_ID,
    fetchPolicy: 'no-cache',
    variables: {
      ...vars,
      currentRuns,
      lastUpdated,
    },
  });
}

export type GroupedBucketedQueryPromise = Promise<
  ApolloQueryResult<GroupedBucketedRunsDeltaQuery_Dd_Dq_G_BrdqQuery>
>;

export function doGroupedBucketedApiQuery(
  vars: QueryVars,
  currentRuns: string[],
  lastUpdated: string,
  cause?: RequestCause
): GroupedBucketedQueryPromise {
  return queryDeltaWithRetryAfter<
    GroupedBucketedRunsDeltaQuery_Dd_Dq_G_BrdqQuery,
    GroupedBucketedRunsDeltaQuery_Dd_Dq_G_BrdqQueryVariables
  >({
    context: deltaQueryContext(cause),
    query: GroupedBucketedRunsDeltaQuery_Dd_Dq_G_BrdqDocument,
    fetchPolicy: 'no-cache',
    variables: {
      ...vars,
      currentRuns,
      lastUpdated,
    },
  });
}

export function doGroupedBucketedApiQueryByInternalId(
  vars: ReturnType<typeof queryVars>,
  currentRuns: string[],
  lastUpdated: string,
  cause?: RequestCause
): GroupedBucketedQueryPromise {
  return queryDeltaWithRetryAfter<
    GroupedBucketedRunsInternalIdDeltaQuery_Dd_Dq_G_BrdqQuery,
    GroupedBucketedRunsInternalIdDeltaQuery_Dd_Dq_G_BrdqQueryVariables
  >({
    context: deltaQueryContext(cause),
    query: GroupedBucketedRunsInternalIdDeltaQuery_Dd_Dq_G_BrdqDocument,
    fetchPolicy: 'no-cache',
    variables: {
      ...vars,
      currentRuns,
      lastUpdated,
    },
  });
}
